import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Input from '../input';
import {
  getMiIdsByDecision,
  getItemsByDecision,
} from '../../utils/apiUtils/inventoryUtils';

/**
 * MI ID typeahead + chip list, with multi-select support.
 *
 * Owns the selected-id set and re-fetches `itemsByDecision` for the FULL
 * set on every add or remove, so the parent never has to guess which ids
 * belong to which rows in the response — it always gets a fresh response
 * for the ids that are currently selected.
 *
 * Flow:
 * 1. As the user types, debounced-search `miIdsByDecision` for matching MI ids.
 * 2. On selecting a suggestion (click) or pressing Enter with a typed id,
 *    add it to the internal id set, fetch `itemsByDecision` for the full
 *    set, and call `onChange(allIds, itemsResp)`.
 *    NOTE: a typed id is only accepted if it matches one of the currently
 *    loaded suggestions — arbitrary/unverified MI ids typed by hand are
 *    rejected. Only ids the backend has actually confirmed exist (via
 *    getMiIdsByDecision) can be added.
 * 3. On removing a chip, drop it from the id set, refetch for whatever
 *    remains (or skip the fetch if nothing remains), and call
 *    `onChange(remainingIds, itemsResp)` — itemsResp is `null` when
 *    remainingIds is empty.
 * 4. This component has no opinion on row shape. The parent decides how to
 *    map `itemsResp` into rows and how to merge those with anything else
 *    (e.g. manually-added rows) it maintains separately.
 */
export default function MiIdSuggestionInput({
  type,
  selectedIds = [],
  onChange,
  projectId = null,
  onError,
  disabled = false,
  placeholder = 'Type MI ID & press Enter',
  pageSize = 10,
  showChips = true,
}) {
  const [miInput, setMiInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [busyId, setBusyId] = useState(null); // id currently being added or removed
  const debounceRef = useRef(null);


  const lastErrorRef = useRef({
    message: '',
    time: 0,
  });

  const reportError = (message) => {
  const now = Date.now();

  // Ignore the same error for 2 seconds
  if (
    lastErrorRef.current.message === message &&
    now - lastErrorRef.current.time < 2000
  ) {
    return;
  }

  lastErrorRef.current = {
    message,
    time: now,
  };

  if (onError) {
    onError(message);
  } else {
    window?.triggerAlert?.({
      title: 'Error',
      type: 'error',
      message,
    });
  }
};

  const fetchSuggestions = async (searchStr) => {
    const trimmed = (searchStr || '').trim();
    if (!trimmed) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setSearchLoading(true);
    try {
      const resp = await getMiIdsByDecision({
        type,
        pageNumber: 0,
        pageSize,
        searchStr: trimmed,
        projectId,
      });
      setSuggestions(resp?.content || []);
      setShowSuggestions(true);
    } catch (err) {
      console.error('MI id suggestion fetch failed:', err);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(miInput);
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miInput, type]);

  const addMiId = async (miId) => {
    const trimmed = String(miId).trim();
    if (!trimmed) return;
    if (selectedIds.some((id) => String(id) === trimmed)) {
      setMiInput('');
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Only allow ids that are present in the currently loaded suggestions —
    // this blocks arbitrary/unverified MI ids typed by hand.
    const isValidSuggestion = suggestions.some(
      (s) => String(s.id) === trimmed
    );
    if (!isValidSuggestion) {
      reportError('Please select a valid MI ID from the suggestions list');
      return;
    }

    const allIds = [...selectedIds.map(String), trimmed];

    setBusyId(trimmed);
    try {
      const itemsResp = await getItemsByDecision({ type, miId: allIds });
      onChange(allIds, itemsResp);
      setMiInput('');
      setSuggestions([]);
      setShowSuggestions(false);
    } catch (err) {
      console.error('itemsByDecision fetch failed:', err);
      reportError('Entered MI ID does not exist or has no eligible items');
    } finally {
      setBusyId(null);
    }
  };

  const removeMiId = async (miId) => {
    const trimmed = String(miId).trim();
    const remainingIds = selectedIds
      .map(String)
      .filter((id) => id !== trimmed);

    if (remainingIds.length === 0) {
      onChange([], null);
      return;
    }

    setBusyId(trimmed);
    try {
      const itemsResp = await getItemsByDecision({
        type,
        miId: remainingIds,
      });
      onChange(remainingIds, itemsResp);
    } catch (err) {
      console.error('itemsByDecision refetch after removal failed:', err);
      reportError('Failed to refresh items after removing MI ID');
    } finally {
      setBusyId(null);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = (e.target.value || '').trim();
      if (val) addMiId(val);
    }
  };

  return (
    <div>
      <div className="relative">
        <Input
          placeholder={placeholder}
          value={miInput}
          disabled={disabled}
          onChange={(e) => setMiInput(e.target.value)}
          onKeyDown={onKeyDown}
        />

        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-md border bg-white shadow-lg">
            {suggestions.map((item) => (
              <div
                key={item.id}
                className="px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm flex items-center justify-between"
                onClick={() => addMiId(item.id)}
              >
                <span className="font-medium">MI ID: {item.id}</span>
                {busyId === String(item.id) && (
                  <span className="text-xs text-gray-400">Loading…</span>
                )}
              </div>
            ))}
          </div>
        )}

        {searchLoading && (
          <div className="absolute right-2 top-2 text-xs text-gray-400">
            Searching…
          </div>
        )}
      </div>

      {showChips && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedIds?.map((id) => (
            <span
              key={String(id)}
              className="inline-flex items-center gap-2 px-3 py-1 border border-orange-400 text-gray-900 rounded"
            >
              <span className="max-w-[140px] truncate">{id}</span>
              <button
                type="button"
                className="text-gray-600 hover:text-gray-800"
                aria-label={`Remove ${id}`}
                disabled={busyId === String(id)}
                onClick={() => removeMiId(id)}
              >
                {busyId === String(id) ? '…' : '×'}
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

MiIdSuggestionInput.propTypes = {
  type: PropTypes.oneOf(['PO', 'LOGISTIC']).isRequired,
  selectedIds: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  pageSize: PropTypes.number,
  projectId: PropTypes.number,
  showChips: PropTypes.bool,
};